import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { EChart } from '../EChart';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { buildFrame, frameNote, isEmpty } from '../frame';
import { cell } from '../format';
import {
  animation,
  BAR_MAX_WIDTH,
  baseGrid,
  categoryAxisX,
  tooltipChrome,
  valueAxisY,
} from '../options';

/**
 * A waterfall — how a starting figure is built up (or drawn down) to an end
 * total by a sequence of gains and losses.
 *
 * Each category's value is a signed delta; the bars float from the running
 * total before to the running total after, so the eye follows the cumulative
 * line up and down. Gains and losses take the up/down delta colours the stat
 * tile uses, so the direction reads without a legend.
 *
 * Built as two stacked bar series — a transparent spacer that lifts each bar to
 * its starting height, and the visible delta on top.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

const MAX_STEPS = 30;

const waterfallView = (data: ChartData, theme: ReturnType<typeof useTheme>, fields?: ChartFields) => {
  const frame = buildFrame(data, { limit: MAX_STEPS, ...fields });
  if (isEmpty(frame)) return null;

  const deltas = frame.series[0].values.map((v) => v ?? 0);
  if (deltas.length < 2) return null;

  let running = 0;
  const spacer: number[] = [];
  const rises: (number | null)[] = [];
  const falls: (number | null)[] = [];
  const totals: number[] = [];
  for (const d of deltas) {
    const before = running;
    running += d;
    totals.push(running);
    spacer.push(Math.min(before, running));
    rises.push(d >= 0 ? d : null);
    falls.push(d < 0 ? -d : null);
  }

  const up = theme.deltaUp;
  const down = theme.deltaDown;

  const option: EChartsOption = {
    ...animation,
    grid: baseGrid({ legend: false, rotatedLabels: frame.categories.length > 8 }),
    xAxis: categoryAxisX(theme, frame, { rotate: frame.categories.length > 8 }),
    yAxis: valueAxisY(theme, { name: frame.measureName }),
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: theme.surfaceSunken, opacity: 0.4 } },
      ...tooltipChrome(theme),
      formatter: (params: unknown) => {
        const list = params as Array<{ dataIndex: number }>;
        const idx = list[0]?.dataIndex ?? 0;
        const root = document.createElement('div');
        root.className = 'chart-tip';
        const head = document.createElement('div');
        head.className = 'chart-tip__title';
        head.textContent = String(frame.categories[idx] ?? '');
        root.append(head);
        const line = (name: string, value: string) => {
          const row = document.createElement('div');
          row.className = 'chart-tip__row';
          const n = document.createElement('span');
          n.className = 'chart-tip__name';
          n.textContent = name;
          const v = document.createElement('strong');
          v.className = 'chart-tip__value';
          v.textContent = value;
          row.append(n, v);
          return row;
        };
        root.append(line('Change', `${deltas[idx] >= 0 ? '+' : ''}${cell(deltas[idx], 'exact')}`));
        root.append(line('Running total', cell(totals[idx], 'exact')));
        return root;
      },
    },
    series: [
      {
        type: 'bar' as const,
        stack: 'wf',
        silent: true,
        itemStyle: { color: 'transparent' },
        emphasis: { itemStyle: { color: 'transparent' } },
        data: spacer,
      },
      {
        type: 'bar' as const,
        name: 'Increase',
        stack: 'wf',
        barMaxWidth: BAR_MAX_WIDTH,
        itemStyle: { color: up, borderRadius: [3, 3, 0, 0] },
        data: rises,
      },
      {
        type: 'bar' as const,
        name: 'Decrease',
        stack: 'wf',
        barMaxWidth: BAR_MAX_WIDTH,
        itemStyle: { color: down, borderRadius: [0, 0, 3, 3] },
        data: falls,
      },
    ],
  };

  return { option, note: frameNote(frame) };
};

export const Waterfall = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => waterfallView(data, theme, fields), [data, theme, fields]);
  if (!view) return <Empty message="Needs a sequence of changes to chart" />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Waterfall chart" />
    </Plot>
  );
};
